<template>
  <div class="form-page position-relative" :class="{'p-3': $store.state.isMobile}">
    <div v-if="loading || !allDataLoaded ">
      <div class="loading-shade" :class="{'compensate': !$store.state.isMobile}">
      </div>
      <pre-loader></pre-loader>
    </div>
    <div v-if="allDataLoaded">
      <div class="header paragraph">
        <div class="title-main">Your Geoff Banks Limits</div>
        <div class="intro">Stay in control of your spending. We offer two financial limits that work in different ways — choose the one that suits you. If you wish to set up or alter a limit, complete the short form below.</div>
        <div v-if="isLimited" class="limit-bar-wr">
          <deposit-limit-bar
            hideArrowLink
            :limit-type="depositLimitData?.limitType"
            :amount-max="minRemainingLimit?.limit"
            :amount-used="minRemainingLimit?.usedAmount"
            :amount-remaining="minRemainingLimit?.remainingLimit"
            :limit-period="minRemainingLimit?.period"
            :limit-end="minRemainingLimit?.periodEnd"
          />
        </div>
      </div>
      
      <div class="section">
        <div class="title-section">Set or update a limit</div>
        <form class="form-limit" @submit.prevent="submitForm" autocomplete="off" novalidate="novalidate">
        
          <div class="field-wr">
            <div class="label">Account Name</div>
            <div class="money-container max-width-field">
              <vue-input
                class="money-input"
                name="deposit-limit"
                type="text"
                v-model="firstName"
                placeholder="First Name"
              />
              <vue-input
                class="money-input"
                name="deposit-limit"
                type="text"
                v-model="lastName"
                placeholder="Last Name"
              />
            </div>
          </div>

          <div class="field-wr">
            <div class="label">Limit Type</div>
            <div class="note">Choose which limit you'd like to set.</div>
            <div class="radio-select-tabs">
              <label v-for="(opt) in ENUMS.depositLimitType" :key="opt.id">
                <input class="radio geoff-radio" type="radio" :value="opt.id" v-model="selectedLimitTypeId">
                <span>{{ opt.label }}</span>
              </label>
            </div>
          </div>
          
          <div class="field-wr">
            <div class="label">Limit Period</div>
            <div class="radio-select-tabs">
              <label v-for="(opt) in limitOptions" :key="opt">
                <input class="radio geoff-radio" type="radio" :value="opt" v-model="selectedLimit">
                <span>{{ opt }}</span>
              </label>
            </div>
          </div>

          <div class="field-wr">
            <div class="label">Limit Amount</div>
            <div class="note">Enter any amount that's right for you - one that reflects funds you can comfortably afford to lose.</div>
            <div class="money-container">
              <vue-input
                class="money-input"
                name="deposit-limit"
                type="number"
                min="0"
                pattern="\d*"
                v-model="pound"
                :placeholder="CURRENCY.main"
              />
              <vue-input
                class="money-input"
                name="deposit-limit"
                type="number"
                min="0"
                pattern="\d*"
                :placeholder="CURRENCY.add"
                v-model="pence"
              />
            </div>
          </div>

          <div class="field-wr">
            <div class="label">Proof Of Funding <span class="note">(optional)</span></div>
            <div class="note">Not required to set a limit. To support higher deposit levels you may confidentially share proof of your source of funds ({{ API.fileValidation.singleMb }}Mb max)</div>
            <vue-file-input
              class="file-input"
              type="file"
              name="proof-of-funding"
              :value="fileName"
              :accept="API.fileValidation.accept"
              @change="loadFile($event)"
              ref="photoId"
            />
          </div>

          <div class="field-wr">
            <vue-checkbox 
              class="checkbox-input"
              v-model="tcAgree"
              description="'terms'"
              name="cbx-agree"
            >
              <div class="agree-terms">I agree to the Geoff Banks Net Deposit Limit <router-link class="footer-link" :to="{name:'info', params: { infoType: 'Terms And Conditions' }}">Terms & Conditions</router-link></div>
            </vue-checkbox>
          </div>

          <vue-button cls="main" class="my-5">Submit</vue-button>

        </form>
      </div>
    
      <!-- compare -->
      <div class="section">
        <div class="title-section">Two ways to limit your spend</div>

        <div class="compare">
          <div class="elem deposit">
            <span class="tag">Deposit Limit</span>
            <strong>Caps what you pay in</strong>
            <div class="muted">Based only on the money you deposit over a chosen period. Withdrawals don't change it — a firm, predictable ceiling on what enters your account.</div>
          </div>
          <div class="elem loss">
            <span class="tag">Net Deposit Limit</span>
            <strong>Caps your net deposits</strong>
            <div class="muted">Based on your deposits minus your withdrawals over a chosen period. Money you withdraw frees up part of your allowance, giving you more flexibility.</div>
          </div>
        </div>
      </div>

      <!-- detail compare -->
      <div class="section">
        <div class="tabs">
          <a 
            v-for="item in ENUMS.depositLimitType"
            class="tab-btn " 
            :class="[{'selected' : selectedLimitTypeId === item.id }, item.name]"
            :key="item.id"
            @click="() => { selectedLimitTypeId = item.id }"  
          >{{item.label}}</a>
        </div>

        <!-- Deposit Limit -->
        <template v-if="this.selectedLimitTypeId === 'Deposit'">
          <div class="tab-content">
            <div class="title-section">What is a Deposit Limit?</div>
            <div class="paragraph">A Deposit Limit caps the total amount of money you can pay into your account over a set period — daily, weekly or monthly. It looks only at deposits. Withdrawals do not give you any allowance back, so the limit is a firm ceiling on what you put in.</div>
            <div class="title-section">How does it work?</div>
            <div class="formula">Available to deposit =  <span class="marked">your limit − total deposits in the period</span></div>
            <div>Once your deposits reach your chosen limit for the period, you cannot deposit again until the period resets.</div>
            <div class="examples">
              <div class="example type-deposit"><b>Daily limit of {{ CURRENCY.symbol }}50:</b> you deposit {{ CURRENCY.symbol }}50 at 12:00 on Wednesday. You cannot deposit again until 00:00 on Thursday – even if you withdraw money in between.</div>
              <div class="example type-deposit"><b>Weekly limit of {{ CURRENCY.symbol }}200:</b> you deposit {{ CURRENCY.symbol }}200 on Wednesday. Your next deposit is allowed at 00:00 the following Monday.</div>
              <div class="example type-deposit"><b>Monthly limit of {{ CURRENCY.symbol }}1,000:</b> if you have deposited {{ CURRENCY.symbol }}400 in the calendar month, you can deposit up to {{ CURRENCY.symbol }}600 more – regardless of any withdrawals you have made. </div>
            </div>
          </div>
        </template>

        <!-- Net Deposit Limit (ex Loss Limit) -->
        <template v-if="this.selectedLimitTypeId === 'Loss'">
          <div class="tab-content">
            <div class="title-section">What is a Net Deposit Limit?</div>
            <div class="paragraph">A Net Deposit Limit caps your <b>net deposits</b> — your deposits minus your withdrawals — over a set period. Because withdrawals are taken into account, money you take out frees up part of your allowance, giving you more flexibility to deposit again before the period ends.</div>
            <div class="title-section">How does it work?</div>
            <div class="formula">Available to deposit = <span class="marked">your limit − deposits + withdrawals in the period</span></div>
            <div>Daily, weekly and monthly Net Deposit Limits are all calculated in the same way.</div>
            <div class="examples">
              <div class="example type-loss"><b>Daily limit of {{ CURRENCY.symbol }}50:</b> you deposit {{ CURRENCY.symbol }}50 at 12:00 on Wednesday. If you then withdraw {{ CURRENCY.symbol }}20, your available deposit rises to {{ CURRENCY.symbol }}20, which you could deposit again before the 24-hour period ends.</div>
              <div class="example type-loss"><b>Weekly limit of {{ CURRENCY.symbol }}200:</b> you deposit {{ CURRENCY.symbol }}200 at 12:00 on Wednesday. If you withdraw {{ CURRENCY.symbol }}50 during the week, your available deposit rises to {{ CURRENCY.symbol }}50 before the 7-day period ends.</div>
              <div class="example type-loss"><b>Monthly limit of {{ CURRENCY.symbol }}1,000:</b> if you deposited {{ CURRENCY.symbol }}400 and withdrew {{ CURRENCY.symbol }}150 in the last 30 days, your available deposit is {{ CURRENCY.symbol }}1,000 − {{ CURRENCY.symbol }}400 + {{ CURRENCY.symbol }}150 = <b>{{ CURRENCY.symbol }}750</b>.</div>
            </div>
            <div class="note-item">
              <svg-icon class="note-icon" icon-name="minor-note"/>
              <span>A Net Deposit Limit is more flexible than a Deposit Limit because withdrawals top your allowance back up. If you want a firm ceiling on what you pay in, choose a Deposit Limit instead.</span>
            </div>
          </div>
        </template>
      </div>

      <!-- Changing your limit -rule -->
      <div class="section rule">
        <div class="title-section">Changing your limit</div>
        <div class="paragraph">These rules apply to both limit types:</div>
        <ul>
          <li><b>Decreasing a limit takes effect immediately.</b> You can lower either limit at any time and it applies straight away.</li>
          <li><b>Increasing a limit takes 24 hours</b> to take effect on your account. This is a deliberate cooling-off period and we are unable to override it.</li>
        </ul>
      </div>

      <!-- FAQs -->
      <div class="section">
        <div class="title-section">Limit FAQs</div>
        <details class="collapsable">
          <summary>What's the difference between a Deposit Limit and a Net Deposit Limit?</summary>
          <div class="collapsable-answer">A Deposit Limit counts only the money you pay in — withdrawals make no difference. A Net Deposit Limit counts your deposits minus your withdrawals, so taking money out frees up part of your allowance. The Deposit Limit is the firmer cap; the Net Deposit Limit is the more flexible one.</div>
        </details>
        <details class="collapsable">
          <summary>I have made a deposit but it's not showing up on my account.</summary>
          <div class="collapsable-answer">This is likely because the deposit would take you above your limit for the current period. Please check the email linked to your account — you should see a message from Geoff Banks Support showing you your options.</div>
        </details>
        <details class="collapsable">
          <summary>I have increased my limit, but it hasn't updated, why?</summary>
          <div class="collapsable-answer">It takes 24 hours to increase either limit. This cooling-off period applies to both limit types and we are unable to override it. Decreases, by contrast, apply immediately.</div>
        </details>
        <!-- <details class="collapsable">
          <summary>Can I set both a Deposit Limit and a Net Deposit Limit?</summary>
          <div class="collapsable-answer">Yes. Use the form below to set or update either limit. Where both apply, a deposit must satisfy both before it can be accepted.</div>
        </details> -->
      
      </div>
      
    </div>

    
  </div>
</template>

<script>
  import { mapGetters } from "vuex";
  import PreLoader from "@/components/UI/PreLoader";
  import VueInput from "@/components/UI/VueInput";
  import VueFileInput from "@/components/UI/VueFileInput";
  import VueButton from "@/components/UI/VueButton";
  import VueCheckbox from "@/components/UI/VueCheckbox";
  import SvgIcon from "@/components/UI/SvgIcon";
  import DepositLimitBar from "@/components/Payments/DepositLimitBar";

  export default {
    name: "UpdateDepositView",
    components: {
      PreLoader,
      VueInput,
      VueFileInput,
      VueButton,
      VueCheckbox,
      SvgIcon,
      DepositLimitBar
    },
    data:()=>{
      return{
        loading: false,
        fileName: null,
        fileData: null,
        pound: {
          type: Number,
          default: null
        },
        pence: {
          type: Number,
          default: null
        },
        selectedLimit: 'Daily',
        limitOptions: ['Daily', 'Weekly', 'Monthly'],
        
        selectedLimitTypeId: 'Deposit',

        tcAgree: false,
        firstName: '',
        lastName: '',

        // current limits info
        isLimitLoaded: false,
        depositLimitData: null,

      }
    },
    computed: {
      ...mapGetters(['CURRENCY']),
      isUserLoaded() {
        return (this.$store.state.user.userInfoLoaded && this.$store.state.user.userDetailsLoaded)
      },
      infoSelectedLimit() {
        return this.ENUMS.depositLimitType.find(el => el.id === this.selectedLimitTypeId)
      },
      
      minRemainingLimit() {
        if (!this.depositLimitData?.limits?.length) {
          return undefined
        }
        return this.depositLimitData.limits.reduce( (acc, curr) => curr.remainingLimit < acc.remainingLimit ? curr : acc, this.depositLimitData.limits[0] || undefined)
      },
      isLimited() {
        return this.depositLimitData?.limitType && this.depositLimitData.limits?.length
      },
      allDataLoaded() {
        return this.isUserLoaded && this.isLimitLoaded
      }
    },
    watch:{
      allDataLoaded(val) {
        console.log('DEBUG allDataLoaded', val)
        if (val) {
          this.setInitValues()
          this.loading = false
        }
      }
    },
    mounted(){
      this.getLimitData()
      if (this.allDataLoaded) {
        this.setInitValues()
        this.loading = false
      }
    },
    methods: {

      //load current limits
      getLimitData() {
        return this.API.getDepositLimit()
          .then(resp => {
            this.depositLimitData = resp
            this.depositLimitData.minDeposit = this.depositLimitData.minDeposit || 10
          })
          .catch(err => {
            console.log('getDepositLimit error', err)
          })
          .finally(() => {
            this.isLimitLoaded = true
          })
      },

      setInitValues() {
        this.firstName = this.$store.state.user.first_name
        this.lastName = this.$store.state.user.last_name

        // prefill from from current limits
        if (this.isLimited) {
          if (this.depositLimitData) {
            this.selectedLimitTypeId = this.depositLimitData.limitType
          }
          if (this.minRemainingLimit) {
            this.selectedLimit = this.minRemainingLimit.period
          }
        }
      },
      
      submitForm() {
        // validate
        // validate amount
        const pound = parseInt(this.pound) || 0
        const pence = parseInt(this.pence) || 0
        const amount = parseFloat(`${pound}.${pence}`)

        if(!this.firstName || !(this.firstName.trim())) {
          this.$toast.warning("Please, enter First Name!")
          return
        }

        if(!this.lastName || !(this.lastName.trim())) {
          this.$toast.warning("Please, enter Last Name!")
          return
        }

        if(amount <= 0 ) {
          this.$toast.warning("Please, enter valid Limit Amount!")
          return
        }

        if(amount < 10) {
          this.$toast.warning(`Minimum limit should be at least ${this.CURRENCY.symbol}10!`)
          return
        }
        // validate file required
        // if(!this.fileData  && !this.fileName ) {
        //   this.$toast.warning("Please, attach Proof Of Funding!")
        //   return
        // }

        // validate file size
        const maxLimit = this.API.fileValidation.singleMb * 1024 *1024
        if(this.fileData && this.fileData.size > maxLimit) {
          this.$toast.warning(`The attached file exceeds the max of ${this.API.fileValidation.singleMb}Mb!`)
          return
        }

        if (this.fileName && this.fileData) {
          const ext = this.fileName.split('.').pop()
          if (!this.API.fileValidation.formats.includes(ext)) {
            this.$toast.warning(this.API.fileValidation.errorMessageByName('The attached'))
            return
          }
        }

         // validate terms
        if(!this.tcAgree ) {
          this.$toast.warning("You must agree to terms and conditions to continue!")
          return
        }

        let formData = new FormData()
        // user info
        formData.append("Firstname", this.firstName)
        formData.append("Lastname", this.lastName)
        formData.append("Username", this.$store.state.user.cust_Ref)
        formData.append("Email", this.$store.state.user.email)
        // period: value, amount (pounds/pence)
        formData.append("NetDepositLimitPeriod", this.selectedLimit)
        formData.append("DepositLimitType", this.selectedLimitTypeId)
        formData.append("Pounds", pound)
        formData.append("Pence", pence)
        // file
        if (this.fileData)  {
          formData.append("OriginalFileName", this.fileName)
          formData.append("FileData", this.fileData)
        }
        formData.append("IsGeoffBanksDepositLimitAgreement", this.tcAgree)
        

        this.loading = true;
        this.API.updateDepositLimit(formData)
          .then((resp) => {
            if (resp?.isFormSent) {
              this.getLimitData()
                .then(() => {
                  this.loading = false
                  // this.setInitValues()
                  this.clearForm()
                })
                .finally(() => {
                  this.$toast.success("Update deposit limit succesfully submited!<br/> You will receive email confirmation shortly.")
                  this.loading = false
                })
            }
            else {
              this.$toast.error(resp?.errorMessage)
            }
          })
          .catch((error) => {
            console.log('DEBUG updateDepositLimit', error?.response)
            if (error?.response?.data?.errorMessage) {
              this.$toast.error(error.response.data.errorMessage)
            }
            else {
              this.$toast.error('Service is unavailable. Please try again later.')
            }
            this.loading = false

          })
          // .finally(()=>{
          //   this.getLimitData()
          //   this.loading = false
          // })
      },

      loadFile(e){
        let files = e.target.files
        if (!files.length) return
        this.fileName = files[0].name
        this.fileData = files[0]
      },

      clearForm() {
        this.fileName = null
        this.fileData = null
        this.pound = null
        this.pence = null
        this.tcAgree = false
        // this.selectedLimit = 'Daily'
        // this.selectedLimitTypeId = 'Deposit'
      }
    }
  }
</script>

<style scoped lang="scss">

  .title-main {
    font-size: 1.5rem;
    
    margin-bottom: 0.5rem;
  }
  .title-section {
    font-size: 1.125rem;
    margin-bottom: 0.5rem;
    font-weight: 700;
    color: var(--geoff-accent);
  }

  .intro {
    color: var(--geoff-compare-text-muted-2);
    margin-bottom: 1rem;
  }


  .compare{
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0.875rem;
    margin: 1rem 0 0.5rem;

    .elem{
      border: 1px solid var(--geoff-compare-border);
      border-radius: 10px;
      padding: 1.25rem;
      background: var(--geoff-compare-panel-bg-1);

      .muted {
        color: var(--geoff-compare-text-muted-1);
        margin-bottom: 0.75rem;
      }

      .tag {
        display: block;
        font-size: 0.6875rem;
        font-weight: 700;
        letter-spacing: 0.08rem;
        text-transform: uppercase;
      }

      &.deposit{
        border-top: 0.125rem solid var(--geoff-compare-1);
        .tag {
          color: var(--geoff-compare-1);
        }
      }
      &.loss{
        border-top: 0.125rem solid var(--geoff-compare-2);

        .tag {
          color: var(--geoff-compare-2);
        }
      }
      
    }
  }

  /* tabs */
  .tabs {
    display: flex;
    gap: 0.875rem;
    margin-top: 1.5rem;
    margin-bottom: 0;

    .tab-btn {
      color: var(--geoff-compare-text-muted-1);
      font-weight:700;
      font-size: 1rem;
      flex: 1;
      background: var(--geoff-compare-panel-bg-1);
      border: 1px solid var(--geoff-compare-border);
      border-bottom: none;
      padding: 0.8125rem;
      border-radius:0.625rem 0.625rem 0 0;
      cursor: pointer;
      text-decoration: none;
      text-align: center;

      &.selected {
        color: var(--geoff-txt-main);
        background: var(--geoff-compare-panel-bg-2);

        &.type-deposit {
          box-shadow: inset 0 -3px 0 var(--geoff-compare-1);
        }
        &.type-loss {
          box-shadow: inset 0 -3px 0 var(--geoff-compare-2);
        }
      }
    }
  }

  .tab-content {
    color: var(--geoff-compare-text-muted-2);
    padding: 1.5rem;
    background: var(--geoff-compare-panel-bg-2);
    border: 1px solid var(--geoff-compare-border);
    border-radius:0 0 0.625rem 0.625rem;
  }

  .formula { 
    font-size: 1rem;
    line-height: 1rem;
    color:var(--geoff-txt-secondary); 
    background: var(--geoff-compare-panel-bg-1);
    border: 1px solid var(--geoff-compare-border);
    border-radius: 0.5rem;
    padding: 0.875rem;
    margin: 0.5rem 0;

    .marked {
      color: var(--geoff-accent);
      font-weight: 600;
      font-family: monospace;
      // font-family:"Barlow",monospace;
      // font-style;
    }
  }

  .example {
    font-size: 0.875rem;
    color: var(--geoff-compare-text-muted-2);
    background: var(--geoff-compare-panel-bg-1);
    border: 1px solid var(--geoff-compare-border);
    border-radius: 0.5rem;
    padding: 0.75rem;
    border-left: 3px solid var(--geoff-bg-neutral);
    margin: 0.75rem 0;

    b {
      color: var(--geoff-txt-main);
    }
    &.type-deposit {
      border-left: 3px solid var(--geoff-compare-1);
    }
    &.type-loss {
      border-left: 3px solid var(--geoff-compare-2);
    }

    
  }

  .note-item {
    font-size: 0.875rem;
    color: var(--geoff-block-note-text);
    display: flex;
    gap: 0.75rem;

    background: var(--geoff-block-note-bg);
    border: 1px solid var(--geoff-block-note-br);
    border-radius:8px;
    padding:13px 15px;
    margin-top:16px;

    .note-icon {
      width: 1.125rem;
      min-width: 1.125rem;
      height: 1.125rem;
      color: var(--geoff-accent);
    }
  }

  .rule {
    color: var(--geoff-compare-text-muted-2);
    background: var(--geoff-compare-panel-bg-1);
    border:1px solid var(--geoff-compare-border);
    border-radius: 0.625rem;
    padding: 1rem; 
    margin-top:1rem;
    b {
      color: var(--geoff-txt-main);
    }
  }

  // FAQ item 
  .collapsable {
    background: var(--geoff-compare-panel-bg-1);
    border:1px solid var(--geoff-compare-border);
    border-radius: 0.5rem;
    margin-bottom: 0.5rem;
    overflow: hidden;

    summary {
      cursor: pointer;
      color: var(--geoff-txt-main);
      padding: 0.75rem;
      font-weight: 700;
      list-style:none;
      display: flex;
      justify-content: space-between;
      align-items: center;
      font-style: italic;

      &::-webkit-details-marker{
        display:none;
      }

      &::after {
        content: "+";
        color: var(--geoff-accent);
        font-size: 1.25rem;
        font-style: normal;
      }

    }
    &[open] summary::after { 
      content:"–";
    }

    .collapsable-answer {
      color: var(--geoff-compare-text-muted-2);
      font-size: 0.875rem;
      padding:0 0.75rem 0.75rem;
    }

  }
  




.form-page {
  min-height: 80vh;

  .paragraph {
    margin-bottom: 1rem;
  }

  .section {
    margin-bottom: 2rem;
  }
  .subheader {
      font-weight: 700;
  }
  .question {
    font-style: italic;
  }

  li {
    list-style: disc;
    margin-left: 1rem;
  }
}

.radio-select-tabs {
  display: flex; 
  gap: 0.5rem;
  flex-wrap: wrap;

  label {
    flex: 1;
    min-width: 140px;
    text-align: center;
    background: var(--geoff-compare-panel-bg-1); 
    border: 1px solid var(--geoff-compare-border);
    border-radius: 0.25rem;
    padding: 0.75rem;
    cursor: pointer;
    position: relative;

    input {
      position:absolute;
      opacity:0;
    }
    &:has(input:checked) {
      border-color: var(--geoff-block-note-br);
      background: var(--geoff-block-note-bg);
      color:var(--geoff-accent);
      font-weight: 700;
    }
  }
}

.limit-bar-wr {
  max-width: 400px;
  margin: 0 auto;
  background-color: var(--geoff-compare-panel-bg-1);
  border: 1px solid var(--geoff-compare-border);
  border-radius: 0.75rem;
  overflow: hidden;
}

.form-limit {
  background-color: var(--geoff-compare-panel-bg-1);
  border: 1px solid var(--geoff-compare-border);
  border-radius: 0.75rem;
  padding: 0.75rem;
}

.field-wr {
  margin-bottom: 1rem;
  .label {
    font-weight: 700;
    margin-bottom: 0.5rem;
    .note {
      font-size: 1rem;
    }
  }
  .radio {
    margin-right: 0.5rem;
  }
  .note {
    font-size: 0.8rem;
    font-weight: 400;
    color: var(--geoff-txt-info);
    margin-bottom: 0.25rem;
  }
  // file input
  .file-input {
    .file-label {
      padding: 0.5rem 4rem 0.5rem 1rem !important;
    }
  }
}

.money-container {
  display: flex;
  align-items: center;
  gap: 1.5rem;
  .money-input {
    margin: 0 0 !important;
  }
}
.agree-terms {
  line-height: 22px;
}
</style>